/**
 * The ONLY renderer module that touches `window.electronAPI.azureStorage.hns*` (S8 Task 8.9). Every request is
 * stamped with a fresh `requestId` (or the caller's, so `useSingleStorageRequest` can cancel it) and a `timeoutMs`,
 * and — when a tab id is given — tracked with that tab like S5's `queueApi.ts`, so closing the tab cancels it.
 * Answers stay `StorageIpcResult`s: dialogs branch on the typed error codes (`DestinationExists`, `PathChanged`, …).
 */
import type { AzureStorageApi, AzureStorageIpcContract, AzureStorageIpcResponse } from '../../../../types/azureStorage';
import { getAzureStorageApi, newStorageRequestId, STORAGE_API_UNAVAILABLE, unwrapStorageResult } from '../services/azureStorageApi';
import { useStorageExplorerStore } from '../store/storageExplorerStore';

export const HNS_REQUEST_TIMEOUT_MS = 30_000;

type HnsChannel = Extract<keyof AzureStorageIpcContract, `azure-storage:hns-${string}`>;
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
/** A request without its lifecycle fields; `requestId` / `timeoutMs` may still be supplied by the caller. */
export type HnsRequest<C extends HnsChannel> = DistributiveOmit<AzureStorageIpcContract[C]['request'], 'requestId' | 'timeoutMs'> & { requestId?: string; timeoutMs?: number };
export type HnsCallOptions = { tabId?: string };

function call<C extends HnsChannel>(
  request: HnsRequest<C>,
  invoke: (api: AzureStorageApi, request: AzureStorageIpcContract[C]['request']) => Promise<AzureStorageIpcResponse<C>>,
  options: HnsCallOptions = {},
): Promise<AzureStorageIpcResponse<C>> {
  const requestId = request.requestId ?? newStorageRequestId();
  const full = { ...request, requestId, timeoutMs: request.timeoutMs ?? HNS_REQUEST_TIMEOUT_MS } as unknown as AzureStorageIpcContract[C]['request'];
  const api = getAzureStorageApi();
  if (!api) return Promise.resolve({ ok: false, error: STORAGE_API_UNAVAILABLE } as AzureStorageIpcResponse<C>);
  const { tabId } = options;
  if (tabId) useStorageExplorerStore.getState().trackRequest(tabId, requestId);
  return invoke(api, full).finally(() => {
    if (tabId) useStorageExplorerStore.getState().untrackRequest(tabId, requestId);
  });
}

export const hnsResolvePath = (request: HnsRequest<'azure-storage:hns-resolve-path'>, options?: HnsCallOptions) =>
  call<'azure-storage:hns-resolve-path'>(request, (api, value) => api.hnsResolvePath(value), options);
export const hnsCreateDirectory = (request: HnsRequest<'azure-storage:hns-create-directory'>, options?: HnsCallOptions) =>
  call<'azure-storage:hns-create-directory'>(request, (api, value) => api.hnsCreateDirectory(value), options);
export const hnsMove = (request: HnsRequest<'azure-storage:hns-move'>, options?: HnsCallOptions) =>
  call<'azure-storage:hns-move'>(request, (api, value) => api.hnsMove(value), options);
export const hnsPendingMoves = (request: HnsRequest<'azure-storage:hns-pending-moves'>, options?: HnsCallOptions) =>
  call<'azure-storage:hns-pending-moves'>(request, (api, value) => api.hnsPendingMoves(value), options);
export const hnsGetAcl = (request: HnsRequest<'azure-storage:hns-get-acl'>, options?: HnsCallOptions) =>
  call<'azure-storage:hns-get-acl'>(request, (api, value) => api.hnsGetAcl(value), options);
export const hnsSetAcl = (request: HnsRequest<'azure-storage:hns-set-acl'>, options?: HnsCallOptions) =>
  call<'azure-storage:hns-set-acl'>(request, (api, value) => api.hnsSetAcl(value), options);
export const hnsAclRecursive = (request: HnsRequest<'azure-storage:hns-acl-recursive'>, options?: HnsCallOptions) =>
  call<'azure-storage:hns-acl-recursive'>(request, (api, value) => api.hnsAclRecursive(value), options);

/** Query helper: unwraps the answer and throws the typed `StorageOperationError` (TanStack Query carries it as `error`). */
export async function unwrapHns<T>(pending: Promise<{ ok: true; value: T } | { ok: false; error: import('../../../../types/azureStorage').StorageOperationError }>): Promise<T> {
  return unwrapStorageResult(await pending);
}
